import {useCallback, useRef, useState} from 'react';
import {createMessageId} from '../../aiMessageGenerator/utils/createMessageId';
import type {ChatAskPrompt, ChatMessage, DraftResult, PendingChatAttachment} from '../../aiMessageGenerator/utils/types';
import type {AiAgentSubmitHandler} from '../types';

export function useAiAgentAsk(submitHandler: AiAgentSubmitHandler) {
    const [pendingAsk, setPendingAsk] = useState<ChatAskPrompt | null>(null);
    const pendingAskRef = useRef<ChatAskPrompt | null>(null);

    const syncPendingAsk = useCallback((ask: ChatAskPrompt | null) => {
        pendingAskRef.current = ask;
        setPendingAsk(ask);
    }, []);

    const clearAsk = useCallback(() => {
        syncPendingAsk(null);
    }, [syncPendingAsk]);

    const applyResult = useCallback((result: DraftResult | null) => {
        if(result === null) {
            return null;
        }
        // Stamp every arriving ask with a fresh id: two consecutive questions can share the
        // same wording, and the compose field must reset between them.
        syncPendingAsk(result.ask ? {...result.ask, id: createMessageId()} : null);
        return result;
    }, [syncPendingAsk]);

    const handleSubmit = useCallback(async (
        prompt: string,
        history: ChatMessage[],
        sessionId: string,
        attachment?: PendingChatAttachment | null,
    ) => {
        const askAtSubmit = pendingAskRef.current;
        if(askAtSubmit) {
            syncPendingAsk(null);
        }
        return applyResult(await submitHandler(prompt, history, sessionId, {
            pendingAsk: askAtSubmit,
            attachment: attachment ?? null,
        }));
    }, [applyResult, submitHandler, syncPendingAsk]);

    return {
        pendingAsk,
        clearAsk,
        handleSubmit,
    };
}
